import React, { useState, useEffect, useMemo, useRef } from 'react';
import { loadStructurePair, getAllPublicStructures, getPublicStructureBySlug } from '../firebase/firestore';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  Building2, CheckCircle, Phone, Mail, ExternalLink, MapPin, Share, Download, MessageCircle, 
  Bus, Car, DoorOpen, Shield, Navigation, Monitor, Camera, Expand, 
  Users, Utensils, Gamepad2, Wrench, HeadphonesIcon, Megaphone, 
  Eye, Volume2, FileText, BookOpen, ArrowLeft, Home, WifiIcon
} from 'lucide-react';
import { ImageWithFallback } from './figma/ImageWithFallback';
import logger from '../utils/logger';

type NavigationSource = 
  | "structure-manager"
  | "portal-manager" 
  | "travelers-hub"
  | null;

interface AccessibilityPassportProps {
  onBack: () => void;
  /** Optional: slug or id for the structure to load */
  structureSlug?: string;
  /** Source of navigation to determine appropriate back button text */
  navigationSource?: NavigationSource;
  /** Branding skin determined by user context */
  branding?: "visit-bergamo" | "default";
  /** When true, renders a print/PDF friendly layout without navigation chrome, stacking all sections */
  exportMode?: boolean;
  /** When exportMode is true, render only this section id instead of all sections (for section-by-section export) */
  exportSectionId?: string;
  /** Optional callback: when exportMode is active and sections are resolved, report ids to parent for export */
  exportOnResolveSections?: (ids: string[]) => void;
}

export function AccessibilityPassport({ onBack, structureSlug, navigationSource, branding = "default", exportMode, exportSectionId, exportOnResolveSections }: AccessibilityPassportProps) {
  const [passportSection, setPassportSection] = useState("general");
  const isVisitBergamo = branding === "visit-bergamo";

  // Canonical keys that should be rendered as URLs
  const URL_KEYS = new Set<string>([
    "12.1", // Dati info azienda - Sito web
    "2.2",  // Mezzi pubblici - link maps
    "4.2",  // Mezzi pubblici - link(s)
    "12.3", // Parcheggi - link maps parcheggi
  ]);

  // Labels (normalized) that identify URL fields
  const URL_LABELS = new Set<string>([
    "sito web",
    "indicare il link a maps",
    "se si indicare i link",
    "link maps parcheggi nelle vicinanze della struttura",
  ]);

  // Keys to hide from the accessibility passport display
  const HIDDEN_KEYS = new Set<string>([
    "1.1", // Rilevatore
    "2.1", // Email rilevatore
  ]);

  // Labels to hide (normalized for comparison)
  const isHiddenLabel = (label: string): boolean => {
    const normalized = (s: string) => 
      (s || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim();
    
    const norm = normalized(label);
    return (
      norm.includes("rilevatore") || 
      norm.includes("email rilevatore") ||
      norm.includes("mail rilevatore")
    );
  };

  // Normalize a key: trim, comma->dot, extract numeric pattern
  const normalizeKey = (raw: string): string => {
    const s = String(raw ?? "").trim().replace(/,/g, ".");
    const m = s.match(/\d+(?:\.\d+)?/);
    return m ? m[0] : s;
  };

  // Normalize a label: lowercase + remove accents
  const normalizeLabel = (raw: string): string => {
    return (raw || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  };

  // Check if a key+label pair identifies a URL field
  const isUrlField = (key: string, label: string): boolean => {
    const normalizedKey = normalizeKey(key);
    const normalizedLabel = normalizeLabel(label);
    
    // First check exact whitelist matches
    if (URL_KEYS.has(normalizedKey) || URL_LABELS.has(normalizedLabel)) {
      return true;
    }
    
    // Robust heuristics for label matching
    if (normalizedLabel.includes('indicare') && normalizedLabel.includes('link')) {
      return true;
    }
    if (normalizedLabel.includes('maps')) {
      return true;
    }
    if (normalizedLabel.includes('sito') && normalizedLabel.includes('web')) {
      return true;
    }
    
    return false;
  };

  // Normalize a URL string: prepend https:// for www., keep http(s) as-is
  const normalizeHref = (raw: string): string => {
    const s = String(raw ?? "").trim();
    if (!s) return "";
    if (/^https?:\/\//i.test(s)) return s;
    if (/^www\./i.test(s)) return `https://${s}`;
    return s;
  };

  // Split possible links by common separators (spaces, commas, semicolons, newlines)
  const splitPossibleLinks = (text: string): string[] =>
    text
      .split(/\s+|,\s*|;\s*|\n+/g)
      .map(t => t.trim())
      .filter(Boolean);

  // Check if a string looks like a URL
  const isHttpLike = (s: string): boolean =>
    /^https?:\/\//i.test(s) || /^www\./i.test(s);

  // Render a URL value (supports multiple links separated by spaces/commas/newlines)
  // Convert full URL to human-readable anchor text
  const prettyLinkText = (fullUrl: string): string => {
    try {
      // Parse the URL safely
      const url = new URL(fullUrl);
      
      // Check if it's a Google Maps URL
      if (url.hostname.includes('maps.google') || url.hostname.includes('google.com/maps')) {
        // Try to extract place name from path or query params
        // Maps URLs typically have /place/{placeName} or place parameter
        const pathMatch = url.pathname.match(/\/place\/([^/]+)/);
        if (pathMatch && pathMatch[1]) {
          let placeName = decodeURIComponent(pathMatch[1]).replace(/\+/g, ' ');
          return `Apri su Google Maps: ${placeName}`;
        }
        // Fallback for Maps URLs without clear place name
        return 'Apri su Google Maps';
      }
      
      // For other URLs: show domain + shortened path
      let label = url.hostname;
      if (url.pathname && url.pathname !== '/') {
        const pathParts = url.pathname.split('/').filter(p => p);
        if (pathParts.length > 0) {
          label += '/' + pathParts.slice(0, 2).join('/');
          if (pathParts.length > 2) {
            label += '�';
          }
        }
      }
      return label;
    } catch (e) {
      // Fallback: truncate the original URL
      return fullUrl.length > 50 ? fullUrl.substring(0, 47) + '�' : fullUrl;
    }
  };

  const renderUrlValue = (rawValue: any): React.ReactNode => {
    const text = String(rawValue ?? "").trim();
    if (!text) return "";

    // Regex to match URLs: http(s):// or www.
    const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;
    const parts: Array<{ type: 'text' | 'url'; content: string }> = [];
    let lastIndex = 0;
    let match;

    while ((match = urlRegex.exec(text)) !== null) {
      // Add any text before this URL
      if (match.index > lastIndex) {
        parts.push({ type: 'text', content: text.slice(lastIndex, match.index) });
      }
      // Add the URL
      parts.push({ type: 'url', content: match[0] });
      lastIndex = match.index + match[0].length;
    }

    // Add any remaining text
    if (lastIndex < text.length) {
      parts.push({ type: 'text', content: text.slice(lastIndex) });
    }

    // If no URLs found, render as plain text
    if (parts.length === 0 || parts.every(p => p.type === 'text')) {
      return text;
    }

    // Render parts with links
    return parts.map((part, i) => {
      if (part.type === 'url') {
        const href = normalizeHref(part.content);
        return (
          <a
            key={`url-${i}`}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline break-all"
          >
            {prettyLinkText(href)}
          </a>
        );
      } else {
        return <React.Fragment key={`text-${i}`}>{part.content}</React.Fragment>;
      }
    });
  };

  // Render field value: URL rendering for URL fields, plain text otherwise
  const renderFieldValue = (key: string, label: string, rawValue: any): React.ReactNode => {
    const valueStr = String(rawValue ?? "");
    if (!valueStr.trim()) return "";

    if (isUrlField(key, label)) {
      return renderUrlValue(valueStr);
    }

    // Plain text for all other fields
    return valueStr;
  };

  // Determine back button text based on navigation source
  const getBackButtonText = () => {
    switch (navigationSource) {
      case "travelers-hub":
        return "Ritorna all'Hub dei viaggiatori";
      case "portal-manager":
        return "Ritorna alla dashboard portale";
      case "structure-manager":
        return "Ritorna alla Dashboard";
      default:
        return "Ritorna alla Dashboard";
    }
  };

  // Real structure presentation data (base identity from static JSON; modules/public_infos primarily from Firestore public doc)
  const [structureData, setStructureData] = useState<any | null>(null);
  const [loadingStructure, setLoadingStructure] = useState(false);
  const [dictionaryIndex, setDictionaryIndex] = useState<Record<string, Record<string, string>> | null>(null);
  const [modulesBible, setModulesBible] = useState<null | Record<string, {
    title: string;
    groups: Array<{ header: string; rows: Array<{ key: string; label: string; hint?: string }> }>;
    keyToLabel: Record<string, string>;
    keyToHint: Record<string, string>;
    orderedKeys: string[];
  }>> (null);
  const [keyRemap, setKeyRemap] = useState<{ oldToNew: Record<string, string>; newToOld: Record<string, string> } | null>(null);

  // Load presentation data directly from Firestore (public + private join) without static fallbacks
  useEffect(() => {
    let mounted = true;
    async function load() {
      setLoadingStructure(true);
      try {
        logger.debug('[Passport] load: start', { slug: structureSlug, exportMode });

        let key = structureSlug || '';
        if (!key) {
          const all = await getAllPublicStructures();
          key = all?.[0]?.id || '';
        }
        if (!key) {
          throw new Error('Nessuna struttura disponibile in Firestore.');
        }

        const normKey = String(key).replace(/\\.json$/i, '');

        let pair: { privateDoc: any; publicDoc: any } = { privateDoc: null, publicDoc: null };
        try {
          pair = await loadStructurePair(normKey);
        } catch (err) {
          logger.warn('[Passport] loadStructurePair failed, fallback to public-only', err);
        }

        let effectivePrivate = pair.privateDoc || null;
        let effectivePublic = pair.publicDoc || null;

        if (!effectivePublic) {
          try {
            const direct = await getPublicStructureBySlug(normKey);
            if (direct) effectivePublic = direct;
          } catch (err) {
            logger.warn('[Passport] getPublicStructureBySlug failed', err);
          }
        }

        if (!effectivePublic) {
          const all = await getAllPublicStructures();
          if (Array.isArray(all) && all.length) {
            effectivePublic = all[0];
          }
        }

        const publicModulesRaw = Array.isArray(effectivePublic?.modules) ? effectivePublic.modules : [];
        const privateModulesRaw = Array.isArray(effectivePrivate?.modules) ? effectivePrivate.modules : [];
        const mergedModulesRaw = publicModulesRaw.length ? publicModulesRaw : privateModulesRaw;

        const normalizedModules = mergedModulesRaw.map((m: any) => {
          const title = m?.type || m?.name || m?.module_name || m?.module_id;
          return {
            ...m,
            type: m?.type || m?.module_name || m?.module_id || title,
            name: m?.name || title,
            module_name: m?.module_name || title,
            content: m?.content || m?.questions || {},
          };
        });

        const mergedPhotos = Array.isArray(effectivePublic?.photos)
          ? effectivePublic.photos
          : Array.isArray(effectivePrivate?.photos)
            ? effectivePrivate.photos
            : [];

        const merged = effectivePublic || effectivePrivate
          ? {
              ...(effectivePrivate || effectivePublic || {}),
              modules: normalizedModules,
              public_infos: effectivePublic?.public_infos ?? effectivePrivate?.public_infos,
              photos: mergedPhotos,
              slug: normKey,
            }
          : null;

        if (!mounted) return;
        setStructureData(merged);
      } catch (err) {
        if (!mounted) return;
        logger.warn('[Passport] load failed', err);
        setStructureData(null);
      } finally {
        if (mounted) setLoadingStructure(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, [structureSlug]);

  // Debug: log when structure data gets resolved, including modules snapshot
  useEffect(() => {
    if (!structureData) return;
    const mods = Array.isArray((structureData as any).modules) ? (structureData as any).modules : [];
    logger.debug('[Passport] structureData resolved', {
      slug: structureData?.slug || structureSlug,
      modulesCount: mods.length,
      moduleNames: mods.map((m: any) => m?.type || m?.name || m?.module_name || m?.module_id || m?.id).filter(Boolean),
    });
  }, [structureData, structureSlug]);

  // Load dictionary once to map module content keys -> human labels
  useEffect(() => {
    let mounted = true;
    async function loadDict() {
      try {
        const res = await fetch('/moduli_questions_dictionary_NORMALIZED.json');
        if (!res.ok) return;
        const arr = await res.json();
        if (!mounted || !Array.isArray(arr)) return;
        const normalize = (s: string) =>
          (s || '')
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '') // strip diacritics
            .replace(/[^a-z0-9]+/g, ''); // remove punctuation and spaces
        const idx: Record<string, Record<string, string>> = {};
        for (const entry of arr) {
          const mod = normalize(entry?.module || '');
          if (!mod) continue;
          const dict = entry?.dictionary || {};
          idx[mod] = dict;
        }
        setDictionaryIndex(idx);
      } catch {
        // ignore, labels will fallback to raw keys
      }
    }
    loadDict();
    return () => { mounted = false; };
  }, []);

  // Load and index ModulesBible to drive ordering/grouping/labels
  useEffect(() => {
    let mounted = true;
    const normalize = (s: string) =>
      (s || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '')
        .replace(/^[0-9]+/, '');
    async function loadBible() {
      try {
        const res = await fetch('/derived/ModulesBible.json');
        if (!res.ok) return;
        const json = await res.json();
        if (!mounted || !json || typeof json !== 'object') return;
        const index: Record<string, any> = {};
        const oldToNew: Record<string, string> = {};
        const newToOld: Record<string, string> = {};
        for (const [sheetName, rows] of Object.entries<any>(json)) {
          if (!Array.isArray(rows)) continue;
          const title = String(sheetName);
          // Parse normalization mapping sheet
          if (normalize(title) === normalize('Normalizzazione e controllo')) {
            for (const r of rows) {
              const vals = Object.values(r || {});
              for (const val of vals) {
                const s = String(val || '');
                // pattern like "49.30 --> 36.3" optionally with trailing text
                const m = s.match(/(\d+[\.,]?\d*)\s*-->\s*(\d+[\.,]?\d*)/);
                if (m) {
                  const from = m[1].replace(',', '.');
                  const to = m[2].replace(',', '.');
                  oldToNew[from] = to;
                  newToOld[to] = from;
                }
              }
            }
            continue;
          }
          // Determine the label column key for this sheet (first non-chiave, non __* property found)
          let labelCol: string | null = null;
          for (const r of rows) {
            if (!r || typeof r !== 'object') continue;
            for (const k of Object.keys(r)) {
              if (k === 'chiave' || k.startsWith('__')) continue;
              labelCol = k;
              break;
            }
            if (labelCol) break;
          }
          if (!labelCol) continue;

          const groups: Array<{ header: string; rows: Array<{ key: string; label: string; hint?: string }> }> = [];
          const keyToLabel: Record<string, string> = {};
          const keyToHint: Record<string, string> = {};
          const orderedKeys: string[] = [];
          let currentGroup: { header: string; rows: Array<{ key: string; label: string; hint?: string }> } | null = null;

          for (const r of rows) {
            const label = String(r[labelCol] ?? '').trim();
            const key = String(r['chiave'] ?? '').trim();
            const hint = r['__EMPTY'] != null ? String(r['__EMPTY']).trim() : '';

            if (!key) {
              if (label) {
                currentGroup = { header: label, rows: [] };
                groups.push(currentGroup);
              }
              continue;
            }

            // Ensure there's a group even if none defined yet
            if (!currentGroup) {
              currentGroup = { header: '', rows: [] };
              groups.push(currentGroup);
            }
            const rowObj = { key, label: label || key, hint: hint || undefined };
            currentGroup.rows.push(rowObj);

            orderedKeys.push(key);
            keyToLabel[key] = label || key;
            if (hint) keyToHint[key] = hint;
          }

          // Build normalized name mapping (strip digits/punct)
          const norm = normalize(title);
          if (!norm) continue;
          index[norm] = { title, groups, keyToLabel, keyToHint, orderedKeys };
        }
        setModulesBible(index);
        setKeyRemap({ oldToNew, newToOld });
      } catch {
        // ignore; feature is optional
      }
    }
    loadBible();
    return () => { mounted = false; };
  }, []);

  // Use fetched data from Firestore (no static fallback)
  const data = structureData;

  // Build navigation dynamically from modules (excluding first: Dati info azienda)
  const modules: any[] = useMemo(() => {
    const arr = Array.isArray((data as any).modules) ? (data as any).modules : [];
    logger.debug('[Passport] modules raw snapshot', {
      slug: (data as any)?.slug || structureSlug,
      count: arr.length,
      moduleNames: arr.map((m: any) => m?.type || m?.name || m?.module_name || m?.module_id || m?.id).filter(Boolean),
    });
    return arr;
  }, [data, structureSlug]);

  const moduleItems: Array<{ id: string; label: string; icon: any; module: any }> = useMemo(() => {
    const resolveTitle = (mod: any, idx: number) =>
      mod?.type || mod?.name || mod?.module_name || mod?.module_id || `Modulo ${idx + 1}`;

    const filtered = modules
      .slice(1)
      .filter((mod) => {
        const c = mod?.content || {};
        const values = Object.values(c ?? {});
        return values.some((v) => {
          if (v === null || v === undefined) return false;
          const s = String(v);
          return s.trim().length > 0;
        });
      })
      .map((mod, idx) => {
        const id = `module-${idx + 1}`; // stable within filtered list
        const label = resolveTitle(mod, idx);
        return { id, label, icon: FileText, module: mod };
      });

    logger.debug('[Passport] moduleItems derived', {
      slug: (data as any)?.slug || structureSlug,
      totalModules: modules.length,
      visibleModules: filtered.length,
      visibleLabels: filtered.map((m) => m.label),
    });

    return filtered;
  }, [modules, data, structureSlug]);

  const passportNavItems = [
    { id: "general", label: "Informazioni generali", icon: Building2 },
    { id: "accessibility", label: "Informazioni sull'accessibilit�", icon: CheckCircle },
    ...moduleItems,
  ];

  // Notify parent about section ids when in export mode (once per slug)
  const sectionIds = useMemo(() => passportNavItems.map(it => it.id), [passportNavItems.length]);
  const lastReportedSlugRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!exportMode || typeof exportOnResolveSections !== 'function') return;
    // If the slug changed, allow reporting again
    const key = structureSlug || 'default';
    const shouldReport = lastReportedSlugRef.current !== key;
    if (shouldReport && sectionIds.length > 0) {
      try {
        exportOnResolveSections(sectionIds);
        lastReportedSlugRef.current = key;
      } catch {}
    }
  }, [exportMode, structureSlug, exportOnResolveSections, sectionIds]);

  // Helper function to get accommodation name from multiple sources
  const getAccommodationName = () => {
    // Helper to extract value from either a string or an object with .value property
    const extractValue = (val: any) => {
      if (val === null || val === undefined) return '';
      if (typeof val === 'object' && val.value !== undefined) {
        return String(val.value);
      }
      return String(val);
    };
    
    // PRIORITIZE "NOME STRUTTURA" (6.1) from "Dati info azienda" module
    if (Array.isArray((data as any)?.modules) && (data as any).modules.length > 0) {
      const firstModule = (data as any).modules[0];
      if (firstModule?.content) {
        // Try both numbered key (6.1) and human-readable key (NOME STRUTTURA)
        let nomeStruttura = extractValue(firstModule.content["6.1"]);
        if (!nomeStruttura) {
          nomeStruttura = extractValue(firstModule.content["NOME STRUTTURA"]);
        }
        if (nomeStruttura && nomeStruttura.trim()) {
          return nomeStruttura;
        }
      }
    }
    
    // Fallback to standard structure path and other fields
    const name = data?.structure?.accommodation_data?.accommodation_name;
    return name || data?.name || data?.slug || data?.id || 'Caricamento...';
  };

  // Helper function to get general info from first module or structure
  const getGeneralInfo = () => {
    const structure = data?.structure?.accommodation_data || {};
    const contacts = structure.contacts || {};
    const address = structure.address || {};
    
    // Helper to extract value from either a string or an object with .value property
    const extractValue = (val: any) => {
      if (val === null || val === undefined) return '';
      if (typeof val === 'object' && val.value !== undefined) {
        return String(val.value);
      }
      return String(val);
    };
    
    // Default values from structure
    let info = {
      name: '',
      type: structure.type || '',
      address: address.street || '',
      city: address.city || '',
      province: address.province || '',
      phone: contacts.phone || '',
      email: contacts.email || '',
      website: contacts.website || '',
      contactPerson: '',
      contactPhone: '',
      contactEmail: ''
    };
    
    // PRIORITIZE data from "Dati info azienda" module, especially "NOME STRUTTURA" (6.1)
    if (Array.isArray((data as any)?.modules) && (data as any).modules.length > 0) {
      const firstModule = (data as any).modules[0];
      if (firstModule?.content) {
        const content = firstModule.content;
        // Try both numbered keys and human-readable keys
        const nameFromModule = extractValue(content["6.1"]) || extractValue(content["NOME STRUTTURA"]);
        const typeFromModule = extractValue(content["7.1"]) || extractValue(content["Tipologia struttura"]);
        const addressFromModule = extractValue(content["9.1"]) || extractValue(content["Indirizzo:"]) || extractValue(content["Indirizzo"]);
        
        info = {
          name: nameFromModule || structure.accommodation_name || data?.name || '',
          type: typeFromModule || info.type,
          address: addressFromModule || info.address,
          city: info.city, // Keep structured city if available
          province: info.province, // Keep structured province if available
          phone: extractValue(content["10.1"]) || info.phone,
          email: extractValue(content["11.1"]) || info.email,
          website: extractValue(content["12.1"]) || info.website,
          contactPerson: extractValue(content["13.1"]) || extractValue(content["16.1"]) || '',
          contactPhone: extractValue(content["14.1"]) || '',
          contactEmail: extractValue(content["15.1"]) || extractValue(content["17.1"]) || ''
        };
        
        // Extract city/province from address if not available separately
        if (!info.city && !info.province && info.address) {
          const addressParts = info.address.split(',');
          if (addressParts.length > 1) {
            const lastPart = addressParts[addressParts.length - 1].trim();
            if (lastPart.includes('(') && lastPart.includes(')')) {
              // Format like "24124 Bergamo (BG)"
              const parts = lastPart.split('(');
              info.city = parts[0].trim().replace(/^\d+\s*/, ''); // Remove postal code
              info.province = parts[1].replace(')', '');
            } else {
              info.city = lastPart;
            }
          }
        }
      } else {
        // Fallback to structure data if no module content
        info.name = structure.accommodation_name || data?.name || '';
      }
    } else {
      // Fallback to structure data if no modules
      info.name = structure.accommodation_name || data?.name || '';
    }
    
    return info;
  };

  const renderSectionById = (sectionId: string) => {
    switch (sectionId) {
      case "general":
        return (
          <div>
            <div className="mb-6">
              <CardTitle>Informazioni generali</CardTitle>
              <CardDescription>Informazioni sull'accessibilit�</CardDescription>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-6">
                {(getGeneralInfo().phone || getGeneralInfo().email || getGeneralInfo().website || getGeneralInfo().contactPerson) && (
                  <div>
                    <h4 className="mb-4 flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      Informazioni di contatto
                    </h4>
                    <div className="space-y-3 pl-6">
                      {getGeneralInfo().phone && (
                        <div className="flex items-center gap-3">
                          <Phone className="w-4 h-4 text-muted-foreground" />
                          <div>
                            <p className="text-s text-muted-foreground">Telefono</p>
                            <p>{getGeneralInfo().phone}</p>
                          </div>
                        </div>
                      )}
                      {getGeneralInfo().email && (
                        <div className="flex items-center gap-3">
                          <Mail className="w-4 h-4 text-muted-foreground" />
                          <div>
                            <p className="text-s text-muted-foreground">Email</p>
                            <p>{getGeneralInfo().email}</p>
                          </div>
                        </div>
                      )}
                      {getGeneralInfo().website && (
                        <div className="flex items-center gap-3">
                          <ExternalLink className="w-4 h-4 text-muted-foreground" />
                          <div>
                            <p className="text-s text-muted-foreground">Sito web</p>
                            <div className="text-primary hover:underline break-all">
                              {renderFieldValue("12.1", "Sito web", getGeneralInfo().website)}
                            </div>
                          </div>
                        </div>
                      )}
                      {getGeneralInfo().contactPerson && (
                        <div className="flex items-center gap-3">
                          <Phone className="w-4 h-4 text-muted-foreground" />
                          <div>
                            <p className="text-s text-muted-foreground">Contatto</p>
                            <p>{getGeneralInfo().contactPerson}</p>
                            {getGeneralInfo().contactPhone && (
                              <p className="text-ss text-muted-foreground">{getGeneralInfo().contactPhone}</p>
                            )}
                            {getGeneralInfo().contactEmail && (
                              <p className="text-ss text-muted-foreground">{getGeneralInfo().contactEmail}</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {(getGeneralInfo().address || getGeneralInfo().city || getGeneralInfo().province) && (
                  <div>
                    <h4 className="mb-4 flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      Dettagli sulla posizione
                    </h4>
                    <div className="pl-6 space-y-2">
                      {getGeneralInfo().address && (
                        <p className="text-s">
                          <span className="text-muted-foreground">Indirizzo: </span>
                          {getGeneralInfo().address}
                        </p>
                      )}
                      {getGeneralInfo().city && (
                        <p className="text-s">
                          <span className="text-muted-foreground">Citt�: </span>
                          {getGeneralInfo().city}
                        </p>
                      )}
                      {getGeneralInfo().province && (
                        <p className="text-s">
                          <span className="text-muted-foreground">Provincia: </span>
                          {getGeneralInfo().province}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-6">
                <div>
                  <h4 className="mb-4 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Punti di forza nell'accessibilit�
                  </h4>
                  <div className="pl-6">
                    {data.public_infos?.titlets && data.public_infos.titlets.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {data.public_infos.titlets.map((strength: string, index: number) => (
                          <Badge key={index} variant="outline" className="text-s">
                            {strength}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-s">Nessun punto di forza nell'accessibilit� specificato.</p>
                    )}
                  </div>
                </div>

                {data?.structure?.portal_administrators?.portal_name && (
                  <div>
                    <h4 className="mb-4 flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      Certificazione & Portale
                    </h4>
                    <div className="pl-6">
                      <Badge variant="secondary" className="mb-2">
                        {data.structure.portal_administrators.portal_name}
                      </Badge>
                      <p className="text-ss text-muted-foreground">
                        Informazioni verificate attraverso questo portale
                      </p>
                    </div>
                  </div>
                )}

                {data?.structure?.accommodation_data?.hotel_stars && (
                  <div>
                    <h4 className="mb-4 flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      Valutazione
                    </h4>
                    <div className="pl-6">
                      <p className="text-s">
                        <span className="text-muted-foreground">Stelle hotel: </span>
                        {data.structure.accommodation_data.hotel_stars} G��
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case "accessibility":
        return (
          <div>
            <div className="mb-6">
              <CardTitle>Informazioni sull'accessibilit�</CardTitle>
              <CardDescription>Caratteristiche e servizi di accessibilit� dettagliati</CardDescription>
            </div>
            
            {/* Accessibility Scores */}
            {/* {data.private_scores && (
              <div className="mb-8">
                <h4 className="mb-4 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Accessibility Scores
                </h4>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  {Object.entries(data.private_scores).map(([category, score]) => {
                    if (!score || score === "") return null;
                    const numScore = typeof score === 'number' ? score : parseFloat(String(score));
                    const percentage = Math.round(numScore * 100);
                    const categoryLabels = {
                      motoria: "Motor",
                      sensoriale: "Sensory", 
                      cognitiva: "Cognitive",
                      alimentare: "Dietary"
                    };
                    
                    return (
                      <div key={category} className="bg-muted/30 rounded-lg p-4 text-center">
                        <div className="text-xxl font-bold text-primary mb-1">{percentage}%</div>
                        <div className="text-s text-muted-foreground">{categoryLabels[category] || category}</div>
                        <div className="w-full bg-muted mt-2 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full transition-all ${
                              percentage >= 80 ? 'bg-green-500' : 
                              percentage >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )} */}

            {/* Accessibility Strengths from public_infos.strength */}
            {data.public_infos?.strength && data.public_infos.strength.length > 0 && (
              <div className="mb-8">
                <h4 className="mb-4 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Punti di forza nell'accessibilit�
                </h4>
                <div className="flex flex-wrap gap-2 mb-4">
                  {data.public_infos.strength.map((strength: string, index: number) => (
                    <Badge key={index} variant="default" className="bg-green-100 text-green-800 border-green-200">
                      {strength}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Detailed Accessibility Information */}
            <div className="space-y-6">
              {Object.entries(data.public_infos?.paragraphs || {}).map(([title, content]) => (
                <div key={title} className="border-b border-border pb-4 last:border-b-0">
                  <h4 className="mb-3 font-medium">{title}</h4>
                  <p className="text-muted-foreground leading-relaxed">{String(content)}</p>
                </div>
              ))}
            </div>
          </div>
        );

      default: {
        // Handle dynamic module sections like "module-<n>"
        if (sectionId.startsWith("module-")) {
          const item = moduleItems.find((it) => it.id === sectionId);
          const module = item?.module as any;
          const content = module?.content as Record<string, any> | undefined;
          if (content) {
            // Prepare label maps
            const normalizeName = (s: string) =>
              (s || '')
                .toLowerCase()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .replace(/[^a-z0-9]+/g, '')
                .replace(/^[0-9]+/, '');

            const moduleTitle = module?.type || module?.name || module?.module_name || module?.module_id || '';
            const moduleKey = normalizeName(moduleTitle);
            const sheet = modulesBible?.[moduleKey] || null;
            const dictMap = dictionaryIndex?.[moduleKey] || null;

            // Filter out empty values - handle both string values and object values with .value property
            const hasValue = (val: any) => {
              if (val === null || val === undefined) return false;
              
              // If it's an object with a 'value' property, check that
              if (typeof val === 'object' && val.value !== undefined) {
                const s = String(val.value).trim();
                return s.length > 0;
              }
              
              // Otherwise treat as string
              const s = String(val).trim();
              return s.length > 0;
            };

            // Extract the actual value from either a string or an object with .value property
            const extractValue = (val: any) => {
              if (val === null || val === undefined) return '';
              
              // If it's an object with a 'value' property, return that
              if (typeof val === 'object' && val.value !== undefined) {
                return String(val.value);
              }
              
              // Otherwise return as string
              return String(val);
            };

            const canonKey = (raw: string) => {
              const t = String(raw || '').trim().replace(',', '.');
              const m = t.match(/\d+(?:\.\d+)?/);
              const base = m ? m[0] : t;
              const mapped = keyRemap?.oldToNew[base] || base;
              return mapped;
            };

            const normalizeUrlValue = (val: any) => {
              const s = String(val ?? '').trim();
              if (!s) return s;
              if (/^https?:\/\//i.test(s)) return s;
              if (/^www\./i.test(s)) return `https://${s}`;
              return s;
            };

            const resolveValueForSheetKey = (canonical: string) => {
              const rawFromOld = keyRemap?.newToOld[canonical];
              if (content.hasOwnProperty(canonical) && hasValue(content[canonical])) {
                return { rawKey: canonical, value: normalizeUrlValue(extractValue(content[canonical])) };
              }
              if (rawFromOld && content.hasOwnProperty(rawFromOld) && hasValue(content[rawFromOld])) {
                return { rawKey: rawFromOld, value: normalizeUrlValue(extractValue(content[rawFromOld])) };
              }
              return null;
            };

            if (sheet) {
              // Render by groups and sheet ordering
              const usedRawKeys = new Set<string>();
              const visibleGroups = sheet.groups.map(g => {
                const rows = g.rows
                  .map(r => {
                    // Skip hidden keys
                    if (HIDDEN_KEYS.has(r.key)) return null;
                    // Skip hidden labels
                    const label = sheet.keyToLabel[r.key] || dictMap?.[r.key] || r.key;
                    if (isHiddenLabel(label)) return null;
                    const match = resolveValueForSheetKey(r.key);
                    if (!match) return null;
                    usedRawKeys.add(match.rawKey);
                    return { ...r, match };
                  })
                  .filter(Boolean) as Array<{ key: string; label: string; hint?: string; match: { rawKey: string; value: any } }>;
                return { header: g.header, rows };
              }).filter(g => g.rows.length > 0);

              // Any remaining keys not in sheet that still have values (and don't map into a sheet key)
              const extraKeys = Object.keys(content).filter(k => {
                // Skip hidden keys
                const mapped = canonKey(k);
                if (HIDDEN_KEYS.has(mapped)) return false;
                // Skip hidden labels
                const label = dictMap?.[mapped] || dictMap?.[k] || k;
                if (isHiddenLabel(label)) return false;
                if (!hasValue(content[k])) return false;
                if (usedRawKeys.has(k)) return false;
                if (sheet.orderedKeys.includes(mapped)) return false; // it should have been captured
                return true;
              });

              return (
                <div>
                  <div className="mb-6">
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      {module.type || module.name || "Module"}
                    </CardTitle>
                    {module.name && module.name !== module.type && (
                      <CardDescription className="mt-2">{module.name}</CardDescription>
                    )}
                  </div>

                  {visibleGroups.length === 0 && extraKeys.length === 0 ? (
                    <div className="text-center py-8">
                      <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">Nessun contenuto disponibile per questa sezione.</p>
                    </div>
                  ) : (
                    <div className="space-y-8">
                      {visibleGroups.map((group, gi) => (
                        <div key={gi}>
                          {group.header && (
                            <h4 className="mb-4 font-medium text-foreground border-b border-border pb-2">
                              {group.header}
                            </h4>
                          )}
                          <div className="space-y-4">
                            {group.rows.map(({ key, match }) => {
                              const label = sheet.keyToLabel[key] || dictMap?.[key] || key;
                              const hint = sheet.keyToHint[key];
                              const value = match.value; // This is already extracted by resolveValueForSheetKey
                              
                              return (
                                <div key={key} className="bg-muted/20 rounded-lg p-4">
                                  <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                                    <div className="sm:w-1/3">
                                      <div className="font-medium text-s">{label}</div>
                                      {hint && hint.toLowerCase() !== 'note' && (
                                        <div className="text-ss text-muted-foreground mt-1 italic">{hint}</div>
                                      )}
                                    </div>
                                    <div className="sm:w-2/3">
                                      <div className="text-s whitespace-pre-wrap break-words">
                                        {renderFieldValue(key, label, value)}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}

                      {extraKeys.length > 0 && (
                        <div>
                          <h4 className="mb-4 font-medium text-foreground border-b border-border pb-2">
                            Informazioni aggiuntive
                          </h4>
                          <div className="space-y-4">
                            {extraKeys.map((k) => {
                              const ck = canonKey(k);
                              const label = sheet.keyToLabel[ck] || dictMap?.[ck] || dictMap?.[k] || k;
                              return (
                              <div key={k} className="bg-muted/20 rounded-lg p-4">
                                <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                                  <div className="sm:w-1/3">
                                    <div className="font-medium text-s break-all">
                                      {label}
                                    </div>
                                  </div>
                                  <div className="sm:w-2/3">
                                    <div className="text-s whitespace-pre-wrap break-words">
                                      {renderFieldValue(ck, label, extractValue(content[k]))}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            }

            // Fallback: dictionary-based labels and simple ordering
            const entries = Object.entries(content)
              .filter(([, v]) => hasValue(v))
              .sort(([a], [b]) => {
                const na = parseFloat(canonKey(a));
                const nb = parseFloat(canonKey(b));
                const aNum = !isNaN(na);
                const bNum = !isNaN(nb);
                if (aNum && bNum) return na - nb;
                if (aNum) return -1;
                if (bNum) return 1;
                return String(a).localeCompare(String(b));
              });
            return (
              <div>
                <div className="mb-6">
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    {module.type || module.name || "Modulo"}
                  </CardTitle>
                  {module.name && module.name !== module.type && (
                    <CardDescription className="mt-2">{module.name}</CardDescription>
                  )}
                </div>
                {entries.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">Nessun contenuto disponibile per questa sezione.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {entries.map(([k, v]) => {
                      const ck = canonKey(k);
                      const label = dictMap?.[ck] || dictMap?.[k] || k;
                      
                      return (
                        <div key={k} className="bg-muted/20 rounded-lg p-4">
                          <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                            <div className="sm:w-1/3">
                              <div className="font-medium text-s break-all">{label}</div>
                            </div>
                            <div className="sm:w-2/3">
                              <div className="text-s whitespace-pre-wrap break-words">
                                {renderFieldValue(ck, label, extractValue(v))}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }
        }
        // Fallback if unmatched
        return (
          <div>
            <CardTitle>Sezione</CardTitle>
            <CardDescription>Seleziona una sezione a sinistra.</CardDescription>
          </div>
        );
      }
    }
  };
// Show loading state while data is being fetched
  if (loadingStructure) {
    return (
      <div className="h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Caricamento del passaporto di accessibilita...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-2">
          <p className="text-muted-foreground">Nessun dato della struttura disponibile.</p>
          <Button variant="outline" onClick={onBack}>Indietro</Button>
        </div>
      </div>
    );
  }
  if (exportMode) {
    logger.debug('[Passport] exportMode ON', { sections: passportNavItems.length });
    // Print-friendly stacked layout: no back/logo, no side navigation, render all sections as cards
    return (
      <div className="bg-white">
        <div className="max-w-[794px] mx-auto p-6">
          <div className="mb-6">
            <h1 className="text-xxl font-semibold">Passaporto di accessibilita</h1>
            <p className="text-l">{getAccommodationName()}</p>
            <div className="mt-2 text-s text-muted-foreground">
              {getGeneralInfo().type && <div>{getGeneralInfo().type}</div>}
              {getGeneralInfo().address && <div>{getGeneralInfo().address}</div>}
            </div>
            {data?.structure?.portal_administrators?.portal_name && (
              <div className="mt-2">
                <Badge variant="secondary">{data.structure.portal_administrators.portal_name}</Badge>
              </div>
            )}
          </div>

          {/* If a specific section is requested for export, render only that one; otherwise render all */}
          {exportSectionId ? (
            <div className="space-y-6">
              {(() => {
                const item = passportNavItems.find(it => it.id === exportSectionId);
                const label = item?.label || exportSectionId;
                return (
                  <Card data-section-id={exportSectionId} className="break-inside-avoid page-break-inside-avoid">
                    <CardHeader className="pb-2">
                      <CardTitle>{label}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {renderSectionById(exportSectionId)}
                    </CardContent>
                  </Card>
                );
              })()}
            </div>
          ) : (
            <div className="space-y-6">
              {passportNavItems.map((item) => (
                <Card key={item.id} data-section-id={item.id} className="break-inside-avoid page-break-inside-avoid">
                  <CardHeader className="pb-2">
                    <CardTitle>{item.label}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {renderSectionById(item.id)}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-background flex flex-col">
      {/* Main Content Container */}
      <div className="flex-1 p-4 min-h-0">
        <div className="h-full max-w-7xl mx-auto flex flex-col">
          {/* Merged Header with Logo, Navigation, and Passport Info */}
          <Card
            className={isVisitBergamo ? "mb-0 rounded-b-none flex-shrink-0 bg-[#0f0f0f] text-white border-[#1c1c1c]" : "mb-0 rounded-b-none flex-shrink-0 text-white border-blue-200/30"}
            style={isVisitBergamo ? undefined : { backgroundColor: "#003c5d" }}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-4">
                {/* Left side: Back Button */}
                <div className="flex items-center">
                  <Button 
                    variant="outline" 
                    onClick={onBack}
                    className={isVisitBergamo ? "flex items-center gap-2 border-white/20 text-white bg-white/10" : "flex items-center gap-2"}
                    size="sm"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    {getBackButtonText()}
                  </Button>
                </div>
                {/* Center: Travelin Logo */}
                <div className="flex-1 flex justify-center">
                  <img 
                    src={isVisitBergamo ? "/visit-bergamo-logo.svg" : "/travelin-logo-dark.svg"}
                    alt={isVisitBergamo ? "Visit Bergamo" : "Travelin Logo"}
                    className="h-8 w-auto"
                  />
                </div>
                {/* Right side: Action Buttons */}
              </div>
              {/* Passport Information */}
              <div className="space-y-3">
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <h1 className="text-xl font-semibold">Passaporto di accessibilita</h1>
                    <h2 className={`text-l ${isVisitBergamo ? "text-foreground" : "text-white"}`}>{getAccommodationName()}</h2>
                    <div className={`mt-1 flex items-center gap-2 text-ss ${isVisitBergamo ? "text-muted-foreground" : "text-blue-100"}`}>
                      <span>Offerto da</span>
                      <img
                        src="/travelin-logo.svg"
                        alt="Travelin"
                        className="h-4 w-auto rounded-sm bg-white px-1 py-0.5"
                      />
                    </div>
                    <div className={`flex items-center gap-4 text-s ${isVisitBergamo ? "text-muted-foreground" : "text-blue-100"}`}>
                      {getGeneralInfo().type && (
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4" />
                          <span>{getGeneralInfo().type}</span>
                        </div>
                      )}
                      {getGeneralInfo().address && (
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          <span>{getGeneralInfo().address}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="text-right space-y-2">
                    {data?.structure?.portal_administrators?.portal_name && (
                      <Badge variant="secondary" className="mb-2">
                        {data.structure.portal_administrators.portal_name}
                      </Badge>
                    )}
                    {data?.structure?.modified_at && (
                      <p className={`text-ss ${isVisitBergamo ? "text-muted-foreground" : "text-blue-100"}`}>
                        Aggiornato: {new Date(data.structure.modified_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Content Area with Three Columns */}
          <div className="flex gap-6 flex-1 min-h-0">
            {/* Main Content */}
            <div className="flex-1 min-h-0">
              <Card className="rounded-t-none h-full">
                <CardContent className="p-0 h-full">
                  <div className="flex h-full">
                    {/* Left Sidebar Navigation - Scrollable */}
                    <div className="w-80 border-r border-border flex flex-col">
                      <div className="p-6 border-b border-border bg-background flex-shrink-0">
                        <CardTitle className="text-l">Navigazione</CardTitle>
                      </div>
                      <div className="flex-1 overflow-y-auto">
                        <nav className="space-y-1 bg-background p-2">
                          {passportNavItems.map(item => (
                            <button
                              key={item.id}
                              onClick={() => setPassportSection(item.id)}
                              className={`w-full flex items-center gap-3 px-4 py-3 text-left rounded-lg cursor-pointer border transition-colors ${
                                isVisitBergamo
                                  ? `${passportSection === item.id ? 'bg-primary text-primary-foreground border-transparent' : 'text-foreground border-transparent hover:bg-accent'}`
                                  : `${passportSection === item.id ? 'bg-orange-50 text-[#f65009] border-orange-200' : 'text-foreground border-transparent hover:bg-[#003c5d] hover:text-white hover:border-white'}`
                              }`}
                            >
                              <item.icon className="w-4 h-4 flex-shrink-0" />
                              <span className="text-s">{item.label}</span>
                            </button>
                          ))}
                        </nav>
                      </div>
                    </div>

                    {/* Right Content Area - Scrollable */}
                    <div className="flex-1 flex flex-col min-h-0">
                      <div className="flex-1 overflow-y-auto p-8">
                        {renderSectionById(passportSection)}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

